import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaperPlaneTilt, CheckCircle, ShieldCheck, Lightning, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { waLink } from "@/lib/brand";

export default function PublicApply() {
  const [params] = useSearchParams();
  const preselect = params.get("service") || "";
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ name: "", mobile: "", email: "", service: preselect, message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/public/services").then((r) => setServices(r.data.popular || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Enter your name"); return; }
    if (!/^\d{10}$/.test(form.mobile.replace(/\D/g, "").slice(-10))) { toast.error("Enter valid 10-digit mobile"); return; }
    if (!form.service) { toast.error("Pick a service"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/leads/public", {
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        service_hint: form.service,
        message: form.message,
        source: "website",
        landing_page: window.location.pathname + window.location.search,
      });
      setDone({ ref: data.id || data.lead_id || "SUBMITTED", name: form.name });
      toast.success("Application received! We'll contact you shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed. Please try WhatsApp instead.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="apply-success">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
          <CheckCircle size={44} weight="fill" className="text-emerald-600" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>Application received!</h1>
        <p className="text-slate-600 mb-3">Thanks {done.name}. Our team will call you within 30 minutes on <span className="font-semibold">{form.mobile}</span>.</p>
        <div className="inline-block bg-slate-50 border border-slate-200 rounded-full px-4 py-2 text-xs font-mono text-slate-700 mb-8">Reference: {String(done.ref).slice(-8).toUpperCase()}</div>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/track" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-[#1e3a8a] text-white text-sm font-semibold">Track this application</Link>
          <a href={waLink(`Hi, I have applied for ${form.service || "a service"} — my name is ${form.name}, mobile ${form.mobile}.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-5 h-11 rounded-full bg-emerald-600 text-white text-sm font-semibold">
            <WhatsappLogo size={14} weight="fill" /> WhatsApp Us
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-14" data-testid="public-apply">
      <div className="grid lg:grid-cols-5 gap-8">
        {/* Left — info */}
        <div className="lg:col-span-2">
          <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">Apply Online</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>Start your application in 30 seconds.</h1>
          <p className="text-slate-600 mb-8">Share a few details and our team will contact you with the exact documents required &amp; the fastest way to complete your service.</p>

          <div className="space-y-4">
            {[
              { icon: Lightning,    title: "Same-day response", desc: "Our team calls within 30 minutes during business hours." },
              { icon: ShieldCheck,  title: "Encrypted & Safe",  desc: "AES-256 encrypted — your data stays private." },
              { icon: CheckCircle,  title: "No hidden fees",    desc: "Clear pricing, transparent government + service charge split." },
            ].map((it) => {
              const Ic = it.icon;
              return (
                <div key={it.title} className="flex gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-full bg-blue-50 flex items-center justify-center">
                    <Ic size={18} className="text-[#1e3a8a]" weight="fill" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{it.title}</div>
                    <div className="text-sm text-slate-600">{it.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-3">
          <Card className="p-6 md:p-8 bg-white border-slate-200 shadow-lg">
            <form onSubmit={submit} className="space-y-4" data-testid="apply-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Full Name *</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11" placeholder="Your name" data-testid="apply-name" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Mobile *</Label>
                  <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="h-11" placeholder="10-digit mobile" data-testid="apply-mobile" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Email (optional)</Label>
                <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11" placeholder="you@example.com" data-testid="apply-email" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Service *</Label>
                <Select value={form.service} onValueChange={(v) => set("service", v)}>
                  <SelectTrigger className="h-11" data-testid="apply-service"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent className="max-h-80">
                    {services.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Message (optional)</Label>
                <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} placeholder="Any specific requirements…" className="resize-none" data-testid="apply-message" />
              </div>

              <Button type="submit" disabled={busy} className="w-full h-12 bg-[#ea580c] hover:bg-orange-700 text-white font-semibold gap-2" data-testid="apply-submit">
                <PaperPlaneTilt size={14} weight="fill" /> {busy ? "Submitting…" : "Submit Application"}
              </Button>
              <div className="text-[11px] text-slate-500 text-center">By submitting, you agree to be contacted on WhatsApp / phone by DigitalSetu team.</div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
